import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { adminTokens } from './adminTokens';

export type ToastVariant = 'success' | 'info' | 'error';

export interface ToastOptions {
    autoClose?: number | false;
    block?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    position?: string;
    [key: string]: any;
}

interface ToastEntry {
    id: number;
    message: string;
    variant: ToastVariant;
    block: boolean;
    dwell: number;
    onOpen?: () => void;
    onClose?: () => void;
}

const MAX_VISIBLE = 2;
const PILL_DWELL = 3500;
const BLOCK_WORD_THRESHOLD = 8;
const EXIT_MS = 300;

let entries: ToastEntry[] = [];
let sequence = 0;
const listeners = new Set<(next: ToastEntry[]) => void>();

const emit = () => listeners.forEach((listener) => listener(entries));

function push(message: any, variant: ToastVariant, options: ToastOptions = {}): number {
    const text = message === null || message === undefined ? '' : String(message);
    const words = text.trim() ? text.trim().split(/\s+/).length : 1;
    const block = options.block !== undefined ? options.block : words > BLOCK_WORD_THRESHOLD;
    const dwell =
        options.autoClose === false
            ? 0
            : typeof options.autoClose === 'number'
                ? options.autoClose
                : block
                    ? Math.min(9000, PILL_DWELL + words * 160)
                    : PILL_DWELL;

    sequence += 1;
    const entry: ToastEntry = {
        id: sequence,
        message: text,
        variant,
        block,
        dwell,
        onOpen: options.onOpen,
        onClose: options.onClose,
    };

    const next = [...entries, entry];
    while (next.length > MAX_VISIBLE) {
        const oldest = next.shift() as ToastEntry;
        oldest.onClose?.();
    }
    entries = next;
    emit();
    return entry.id;
}

function dismiss(id?: number) {
    if (id === undefined) {
        entries.forEach((entry) => entry.onClose?.());
        entries = [];
    } else {
        const found = entries.find((entry) => entry.id === id);
        if (!found) return;
        found.onClose?.();
        entries = entries.filter((entry) => entry.id !== id);
    }
    emit();
}

export const toast = {
    success: (message: any, options?: ToastOptions) => push(message, 'success', options),
    error: (message: any, options?: ToastOptions) => push(message, 'error', options),
    info: (message: any, options?: ToastOptions) => push(message, 'info', options),
    warning: (message: any, options?: ToastOptions) => push(message, 'info', options),
    warn: (message: any, options?: ToastOptions) => push(message, 'info', options),
    dismiss,
};

const ICONS: Record<ToastVariant, JSX.Element> = {
    success: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <path d="M5 13l4 4L19 7" />
        </svg>
    ),
    info: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
            <circle cx="12" cy="12" r="2.5" />
        </svg>
    ),
    error: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M6 6l12 12M18 6L6 18" />
        </svg>
    ),
};

const ACCENT: Record<ToastVariant, string> = {
    success: adminTokens.green600,
    info: '#2563EB',
    error: adminTokens.errorSolid,
};

const HOST_Z_INDEX = 999999999;

const CSS = [
    '.hz-toast-host{position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:' + HOST_Z_INDEX + ';',
    'display:flex;flex-direction:column;align-items:center;gap:10px;pointer-events:none;',
    'font-family:' + adminTokens.fontSans + '}',
    '.hz-toast{position:relative;display:inline-flex;align-items:center;gap:10px;height:44px;',
    'padding:8px 20px 8px 8px;border-radius:' + adminTokens.radiusFull + ';',
    'background:rgba(255,255,255,.72);-webkit-backdrop-filter:blur(14px) saturate(160%);',
    'backdrop-filter:blur(14px) saturate(160%);',
    'color:' + adminTokens.navy800 + ';font-size:' + adminTokens.textBase + 'px;font-weight:500;white-space:nowrap;',
    'box-shadow:0 10px 28px rgba(12,20,38,.14),inset 0 1px 0 rgba(255,255,255,.75);',
    'opacity:0;transform:translateY(-12px) scale(.94);',
    'transition:opacity .22s ease,transform .3s cubic-bezier(.16,1,.3,1)}',
    '.hz-toast.is-show{opacity:1;transform:translateY(0) scale(1)}',
    '.hz-toast__icon{width:28px;height:28px;border-radius:50%;flex-shrink:0;color:#fff;',
    'display:inline-flex;align-items:center;justify-content:center}',
    '.hz-toast__icon svg{width:15px;height:15px}',
    '.hz-toast--success .hz-toast__icon{background:' + ACCENT.success + '}',
    '.hz-toast--info .hz-toast__icon{background:' + ACCENT.info + '}',
    '.hz-toast--error .hz-toast__icon{background:' + ACCENT.error + '}',
    '.hz-toast--success{--hz-toast-accent:' + ACCENT.success + '}',
    '.hz-toast--info{--hz-toast-accent:' + ACCENT.info + '}',
    '.hz-toast--error{--hz-toast-accent:' + ACCENT.error + '}',
    '.hz-toast--block{height:auto;min-height:44px;max-width:420px;border-radius:' + adminTokens.radiusLg + ';',
    'padding:12px 18px 14px 10px;align-items:flex-start;gap:10px;white-space:normal;',
    'line-height:1.45;text-align:left}',
    '.hz-toast--block .hz-toast__icon{margin-top:1px}',
    '@keyframes hz-toast-pop{0%{transform:scale(.5);opacity:0}60%{transform:scale(1.08);opacity:1}100%{transform:scale(1);opacity:1}}',
    '.hz-toast.is-show .hz-toast__icon{animation:hz-toast-pop .34s cubic-bezier(.34,1.56,.64,1) .05s both}',
    '.hz-toast__trace{position:absolute;inset:0;pointer-events:none;overflow:visible}',
    '.hz-toast__trace .hz-trace-track{fill:none;stroke:rgba(19,28,51,.12);stroke-width:2}',
    '.hz-toast__trace .hz-trace-run{fill:none;stroke:var(--hz-toast-accent,' + adminTokens.gray500 + ');stroke-width:2;',
    'stroke-linecap:round;stroke-dasharray:1;stroke-dashoffset:1;',
    'animation:hz-toast-trace var(--hz-toast-dwell,' + PILL_DWELL + 'ms) linear forwards}',
    '@keyframes hz-toast-trace{to{stroke-dashoffset:0}}',
    '@media (prefers-reduced-motion:reduce){',
    '.hz-toast{transform:none;transition:opacity .15s ease}',
    '.hz-toast.is-show{transform:none}',
    '.hz-toast.is-show .hz-toast__icon{animation:none}',
    '.hz-toast__trace .hz-trace-run{animation:none;stroke-dashoffset:0;opacity:.5}}',
].join('\n');

function ToastPill({ entry, onDone }: { entry: ToastEntry; onDone: (id: number) => void }) {
    const ref = useRef<HTMLDivElement>(null);
    const [show, setShow] = useState(false);
    const [size, setSize] = useState<{ w: number; h: number } | null>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        setSize({ w: el.offsetWidth, h: el.offsetHeight });
    }, []);

    useEffect(() => {
        entry.onOpen?.();
        let inner = 0;
        const outer = requestAnimationFrame(() => {
            inner = requestAnimationFrame(() => setShow(true));
        });
        const fade = entry.dwell ? setTimeout(() => setShow(false), entry.dwell) : undefined;
        const remove = entry.dwell ? setTimeout(() => onDone(entry.id), entry.dwell + EXIT_MS) : undefined;
        return () => {
            cancelAnimationFrame(outer);
            cancelAnimationFrame(inner);
            if (fade) clearTimeout(fade);
            if (remove) clearTimeout(remove);
        };
    }, []);

    const inset = 1;
    const rx = size ? (entry.block ? 12 : (size.h - inset * 2) / 2) : 0;

    return (
        <div
            ref={ref}
            className={
                'hz-toast hz-toast--' + entry.variant +
                (entry.block ? ' hz-toast--block' : '') +
                (show ? ' is-show' : '')
            }
            style={{ ['--hz-toast-dwell' as any]: (entry.dwell || PILL_DWELL) + 'ms' }}
        >
            <span className="hz-toast__icon">{ICONS[entry.variant]}</span>
            <span>{entry.message}</span>
            {size ? (
                <svg
                    className="hz-toast__trace"
                    viewBox={'0 0 ' + size.w + ' ' + size.h}
                    width={size.w}
                    height={size.h}
                    aria-hidden="true"
                >
                    <rect className="hz-trace-track" x={inset} y={inset} width={size.w - inset * 2} height={size.h - inset * 2} rx={rx} />
                    <rect className="hz-trace-run" x={inset} y={inset} width={size.w - inset * 2} height={size.h - inset * 2} rx={rx} pathLength={1} />
                </svg>
            ) : null}
        </div>
    );
}

/** One host per document, mounted at the app root. */
export function ToastHost() {
    const [list, setList] = useState<ToastEntry[]>(entries);

    useEffect(() => {
        listeners.add(setList);
        setList(entries);
        return () => {
            listeners.delete(setList);
        };
    }, []);

    const remove = (id: number) => {
        const found = entries.find((entry) => entry.id === id);
        if (!found) return;
        found.onClose?.();
        entries = entries.filter((entry) => entry.id !== id);
        emit();
    };

    return (
        <>
            <style>{CSS}</style>
            <div className="hz-toast-host" aria-live="polite" aria-atomic="true">
                {list.map((entry) => (
                    <ToastPill key={entry.id} entry={entry} onDone={remove} />
                ))}
            </div>
        </>
    );
}

export default toast;
